import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  operationsAPI,
  tripsAPI,
  trucksAPI,
  driversAPI,
  financeAPI,
} from '../lib/api';
import { messageErreur } from '../lib/erreurs';
import { libelle, nomPays, texteSur } from '../lib/libelles';
import { ArrowLeft, ArrowRight, Clock, Edit } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';
import {
  cn,
  TRUCK_STATUS_LABELS,
  formatCurrency,
  formatDate,
  formatRelativeTime,
  getStatusBadgeClass,
} from '../lib/utils';
import BusinessActionContract from '../components/business/BusinessActionContract';
import { tripStatusContract } from '../lib/businessContracts';
import { PageHeader } from '../components/ui/page-header';
import { LoadingState } from '../components/ui/loading-state';
import { RecordSplit } from '../components/ui/page-layouts';
import { fetchAllPages } from '../lib/fetchAllPages';
import ExpenseForm from '../components/finance/ExpenseForm';
import ExpensesTable from '../components/finance/ExpensesTable';
import ObservationsPanel from '../components/observations/ObservationsPanel';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from '../components/ui/pagination-bar';

// Date du jour dans le fuseau de l'exploitant, au format ISO des champs date.
const localToday = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * Un convoi est en retard quand son retour prévu est passé alors qu'au moins un
 * de ses trajets n'est ni terminé ni annulé. Même règle que l'alerte serveur
 * « retour de convoi en retard ».
 */
export const isReturnOverdue = (operation, trips, today = localToday()) => {
  const planned = String(operation?.planned_return_date || '').slice(0, 10);
  if (!planned || planned >= today) return false;
  return (trips || []).some(
    (trip) => trip.status !== 'COMPLETED' && trip.status !== 'CANCELLED',
  );
};

const readinessLabel = (item) => {
  if (!item) return null;
  if (item.key === 'documents' && !item.ready) return 'Pièce manquante';
  if (item.key === 'reconciliation' && !item.ready) return 'Écart volume à valider';
  if (item.key === 'gps') return item.ready ? 'Dernier point GPS fiable' : 'GPS à vérifier';
  return texteSur(item.label, 'Contrôle');
};

const OperationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [operation, setOperation] = useState(null);
  const [trips, setTrips] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [readinessByTrip, setReadinessByTrip] = useState({});
  const [workflowByTrip, setWorkflowByTrip] = useState({});
  // Recette, dépenses et marge du convoi. Le serveur les calcule depuis
  // l'origine; cet écran ne les demandait pas.
  const [argent, setArgent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [showTripStatusModal, setShowTripStatusModal] = useState(false);
  const [showTruckStateModal, setShowTruckStateModal] = useState(false);
  const [activeTripId, setActiveTripId] = useState(null);
  const [activeTruckId, setActiveTruckId] = useState(null);
  const [newTripStatus, setNewTripStatus] = useState('');
  const [newTruckState, setNewTruckState] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Frais du convoi: saisie ouverte à la demande, tableau rechargé après chaque frais.
  const [fraisOuverts, setFraisOuverts] = useState(false);
  const [fraisVersion, setFraisVersion] = useState(0);

  const loadData = useCallback(async ({ silencieux = false } = {}) => {
    try {
      if (!silencieux) setIsLoading(true);
      const [op, tripsPage, trucksPage, driversPage] = await Promise.all([
        operationsAPI.getById(id),
        fetchAllPages((params) => tripsAPI.getAllPaged({ ...params, operation_id: id })),
        // Les noms de camions et de chauffeurs se cherchent dans toute la
        // flotte: une page de 200 laissait des trajets sans plaque ni nom.
        fetchAllPages((params) => trucksAPI.getAllPaged(params)),
        fetchAllPages((params) => driversAPI.getAllPaged(params)),
      ]);
      setOperation(op);
      setTrips(tripsPage.items || []);
      setTrucks(trucksPage.items || []);
      setDrivers(driversPage.items || []);

      const tripRows = tripsPage.items || [];
      const readinessPairs = await Promise.all(
        tripRows.map(async (trip) => {
          try {
            const readiness = await tripsAPI.getReadiness(trip.id);
            return [trip.id, readiness];
          } catch {
            return [trip.id, null];
          }
        })
      );
      const workflowPairs = await Promise.all(
        tripRows.map(async (trip) => {
          try {
            const workflow = await tripsAPI.getWorkflow(trip.id);
            return [trip.id, workflow];
          } catch {
            return [trip.id, null];
          }
        })
      );
      setReadinessByTrip(Object.fromEntries(readinessPairs));
      setWorkflowByTrip(Object.fromEntries(workflowPairs));

      // Le convoi n'affichait aucun chiffre, alors que le serveur calcule sa
      // recette, ses dépenses et sa marge depuis l'origine — les deux ne se
      // parlaient simplement pas.
      try {
        const resume = await financeAPI.getSummary({ convoi_id: id });
        const ligne = (resume?.by_convoi || []).find(
          (item) => item.operation_id === id,
        );
        setArgent(ligne || null);
      } catch {
        setArgent(null);
      }
    } catch (err) {
      console.error('Failed to load operation detail:', err);
      toast.error(messageErreur(err, 'Impossible de charger le détail du convoi.'));
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) loadData();
  }, [id, loadData]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['convois', 'trajets', 'depenses', 'camions', 'chauffeurs', 'finance'], (options) => (id ? loadData(options) : undefined));


  const getTruck = (truckId) => trucks.find((t) => t.id === truckId);
  const getDriver = (driverId) => drivers.find((d) => d.id === driverId);

  const activeTrip = activeTripId
    ? trips.find((t) => t.id === activeTripId)
    : null;

  const tripStatusOptions = useMemo(() => {
    if (!activeTrip) return [];
    return workflowByTrip[activeTrip.id]?.actions || [];
  }, [activeTrip, workflowByTrip]);
  const selectedWorkflowAction = tripStatusOptions.find((item) => item.action_code === newTripStatus);
  const activeReadiness = activeTrip ? readinessByTrip[activeTrip.id] : null;
  const activeBlockers = (activeReadiness?.checklist || [])
    .filter((item) => !item.ready && item.status === 'blocked')
    .map((item) => texteSur(item.reason || item.label, ''))
    .filter(Boolean);

  const openTripStatus = (trip) => {
    setActiveTripId(trip.id);
    setNewTripStatus('');
    setShowTripStatusModal(true);
  };

  const openTruckState = (truck) => {
    if (!truck?.id) return;
    setActiveTruckId(truck.id);
    setNewTruckState(truck.current_status || 'FREE');
    setShowTruckStateModal(true);
  };

  const handleTripStatusUpdate = async () => {
    if (!activeTripId || !newTripStatus) return;
    setIsSubmitting(true);
    try {
      await tripsAPI.transitionWorkflow(activeTripId, {
        action_code: newTripStatus,
      });
      toast.success('Étape validée.');
      setShowTripStatusModal(false);
      setActiveTripId(null);
      setNewTripStatus('');
      await loadData();
    } catch (err) {
      console.error('Failed to update trip status:', err);
      toast.error(messageErreur(err, 'Cette étape ne peut pas être validée pour le moment.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTruckStateUpdate = async () => {
    if (!activeTruckId || !newTruckState) return;
    setIsSubmitting(true);
    try {
      await trucksAPI.update(activeTruckId, {
        current_status: newTruckState,
      });
      toast.success('État du camion mis à jour');
      setShowTruckStateModal(false);
      setActiveTruckId(null);
      await loadData();
    } catch (err) {
      console.error('Failed to update truck state:', err);
      toast.error(messageErreur(err, 'L’état du camion n’a pas pu être mis à jour.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedTrips = useMemo(() => {
    return [...trips].sort((a, b) =>
      (b.created_at || '').localeCompare(a.created_at || '')
    );
  }, [trips]);
  const pagination = usePagination(sortedTrips, { cle: 'trajets' });

  const returnOverdue = isReturnOverdue(operation, trips);
  // Le nom du chef est recopié sur le convoi à l'enregistrement; si le serveur
  // ne l'a pas renvoyé, on retombe sur la fiche du chauffeur.
  const chiefName =
    operation?.convoy_chief_name ||
    (operation?.convoy_chief_driver_id
      ? getDriver(operation.convoy_chief_driver_id)?.full_name
      : null);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="detail" />
      </div>
    );
  }

  if (!operation) {
    return (
      <div className="p-2 md:p-3">
        <Button variant="ghost" onClick={() => navigate('/operations')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <p className="text-muted-foreground mt-4">Convoi introuvable.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="operation-detail-page">
      <PageHeader
        eyebrow="Convoi"
        title={operation.code}
        description={operation.route || undefined}
        backTo="/operations"
        backLabel="Retour aux convois"
        // Le serveur calcule la recette, les dépenses et la marge du convoi:
        // elles tiennent en trois puces dans le coin de l'en-tête.
        stats={
          argent && (argent.revenue > 0 || argent.expenses > 0)
            ? [
                { label: 'Coûté', valeur: formatCurrency(argent.expenses) },
                { label: 'Rapporté', valeur: formatCurrency(argent.revenue) },
                {
                  label: 'Marge',
                  valeur: formatCurrency(argent.margin),
                  ton: argent.margin < 0 ? 'critique' : 'neutre',
                },
              ]
            : undefined
        }
        alertesSousTitre
        alertes={[
          returnOverdue
            ? {
                niveau: 'attention',
                court: 'Retour en retard',
                texte:
                  'Le retour prévu de ce convoi est dépassé : au moins un camion n’est pas rentré.',
              }
            : null,
        ]}
      />

      {/* Colonne de droite fixe à partir de xl: l'état du convoi, ses frais et
          ses observations restent sous la barre du haut pendant qu'on fait
          défiler les trajets, et défilent dans la colonne si elle dépasse
          l'écran. En dessous de xl tout s'empile: état, trajets, frais,
          observations — pas de défilement imbriqué. */}
      <RecordSplit
        aside={
          <Card className="card-base">
            <CardContent className="pt-4">
              {/* Le statut à gauche, le nombre de camions à droite: les deux tiennent
                  sur une ligne à toute largeur, sans pastille ni mot isolé. */}
              <div className="flex items-center justify-between gap-3">
                <Badge
                  className={cn(
                    'status-badge',
                    operation.active && !operation.closed_at
                      ? 'bg-success/15 text-success border-success/40'
                      : 'bg-muted text-muted-foreground border-border'
                  )}
                >
                  {operation.closed_at ? 'Clôturé' : operation.active ? 'En cours' : 'Clos'}
                </Badge>
                {Array.isArray(operation.truck_ids) ? (
                  <span className="text-xs text-muted-foreground">
                    {operation.truck_ids.length} camion{operation.truck_ids.length > 1 ? 's' : ''}
                  </span>
                ) : null}
              </div>
              <dl
                className="mt-3 space-y-1.5 text-sm"
                data-testid="operation-convoy-info"
              >
                <div className="flex justify-between gap-3">
                  <dt className="shrink-0 text-muted-foreground">Départ</dt>
                  <dd className="min-w-0 font-medium">
                    {operation.departure_date
                      ? formatDate(operation.departure_date)
                      : 'non précisé'}
                  </dd>
                </div>
                {operation.planned_return_date ? (
                  <div className="flex justify-between gap-3">
                    <dt className="shrink-0 text-muted-foreground">Retour prévu</dt>
                    <dd className="min-w-0 font-medium">
                      {formatDate(operation.planned_return_date)}
                    </dd>
                  </div>
                ) : null}
                {operation.closed_at ? (
                  <div className="flex justify-between gap-3">
                    <dt className="shrink-0 text-muted-foreground">Clôturé le</dt>
                    <dd className="min-w-0 font-medium">{formatDate(operation.closed_at)}</dd>
                  </div>
                ) : null}
                {chiefName || operation.convoy_chief_phone ? (
                  <div className="flex justify-between gap-3">
                    <dt className="shrink-0 text-muted-foreground">Chef de convoi</dt>
                    <dd className="min-w-0 break-words text-right font-medium">
                      {chiefName ? <span>{chiefName}</span> : null}
                      {operation.convoy_chief_phone ? (
                        <a
                          href={`tel:${String(operation.convoy_chief_phone).replace(/\s+/g, '')}`}
                          className="block text-primary underline-offset-4 hover:underline"
                          data-testid="operation-chief-phone"
                        >
                          {operation.convoy_chief_phone}
                        </a>
                      ) : null}
                    </dd>
                  </div>
                ) : null}
              </dl>
              {operation.description && (
                <p className="text-xs text-muted-foreground mt-2">
                  {operation.description}
                </p>
              )}
            </CardContent>
          </Card>
        }
        asideSuite={
          <>
            {/* Étape du parcours: ce que le convoi a coûté, puis ce qu'on a noté dessus. */}
            <Card className="card-base">
              <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
                <CardTitle>Frais du convoi</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={() => setFraisOuverts((ouvert) => !ouvert)}>
                  {fraisOuverts ? 'Fermer la saisie' : 'Ajouter un frais'}
                </Button>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                {fraisOuverts && (
                  <ExpenseForm convoyId={id} colonneEtroite onSaved={() => setFraisVersion((v) => v + 1)} />
                )}
                <ExpensesTable convoyId={id} refreshKey={fraisVersion} compact />
              </CardContent>
            </Card>

            <Card className="card-base">
              <CardHeader>
                <CardTitle>Observations</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ObservationsPanel entityType="convoy" entityId={id} colonneEtroite />
              </CardContent>
            </Card>
          </>
        }
        main={
          <Card className="card-base">
            <CardHeader className="pb-3">
              <CardTitle>Trajets liés</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {sortedTrips.length === 0 ? (
                <p className="text-muted-foreground">Aucun trajet pour ce convoi.</p>
              ) : (
                <div className="space-y-2">
                  {pagination.lignes.map((trip) => {
                    const truck = getTruck(trip.truck_id);
                    const driver = getDriver(trip.driver_id);
                    const readiness = readinessByTrip[trip.id];
                    const blockingItems = (readiness?.checklist || []).filter((item) => !item.ready && item.status === 'blocked');
                    const watchItems = (readiness?.checklist || []).filter((item) => item.status === 'watch');
                    const workflowAction = workflowByTrip[trip.id]?.actions?.[0];
                    return (
                      <Card
                        key={trip.id}
                        className="card-base card-interactive"
                        onClick={() => navigate(`/trips/${trip.id}`)}
                        data-testid={`operation-trip-${trip.id}`}
                      >
                        <CardContent className="p-3">
                          {/* Sur téléphone le texte passe d'abord, les boutons dessous: côte à
                              côte ils sortaient de la carte. Dès 640 px, retour à deux colonnes. */}
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <span>{nomPays(trip.departure_country)}</span>
                                <ArrowRight className="w-4 h-4 text-muted-foreground/70" />
                                <span>{nomPays(trip.destination_country)}</span>
                              </div>
                              {/* Chaque repère reste d'un bloc: la date ne se coupe plus entre
                                  « Départ : » et le jour quand la ligne se replie. */}
                              <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                                <span className="whitespace-nowrap font-mono font-bold">
                                  {truck?.matricule || 'Camion non précisé'}
                                </span>
                                {driver?.full_name ? (
                                  <span>{driver.full_name}</span>
                                ) : null}
                                <span className="whitespace-nowrap">
                                  Départ : {formatDate(trip.departure_date)}
                                </span>
                              </div>

                              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                                <Badge
                                  className={cn(
                                    'status-badge',
                                    getStatusBadgeClass(trip.status)
                                  )}
                                >
                                  {libelle('tripStatus', trip.status)}
                                </Badge>
                                <Badge
                                  className={cn(
                                    'status-badge',
                                    truck?.current_status === 'FREE'
                                      ? 'bg-success/15 text-success border-success/40'
                                      : truck?.current_status === 'ON_TRIP'
                                      ? 'bg-flame/15 text-flame-ink border-flame/40'
                                      : truck?.current_status === 'MAINTENANCE'
                                      ? 'bg-flame/15 text-flame-ink border-flame/40'
                                      : 'bg-muted text-muted-foreground border-border'
                                  )}
                                >
                                  {truck?.current_status
                                    ? libelle('truckStatus', truck.current_status)
                                    : '—'}
                                </Badge>
                                {blockingItems.slice(0, 2).map((item) => (
                                  <Badge key={item.key} className="status-badge bg-flame/15 text-flame-ink border-flame/40">
                                    {readinessLabel(item)}
                                  </Badge>
                                ))}
                                {blockingItems.length === 0 && watchItems.slice(0, 1).map((item) => (
                                  <Badge key={item.key} className="status-badge bg-info/15 text-info border-info/40">
                                    {readinessLabel(item)}
                                  </Badge>
                                ))}
                              </div>
                              {readiness?.summary && (
                                <p className="mt-2 text-xs text-muted-foreground">
                                  Prochaine étape : {texteSur(workflowAction?.action_label || readiness.summary.next_action, 'à définir')}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 sm:items-end">
                              <div className="flex flex-wrap items-center gap-2 max-sm:[&>button]:flex-1 sm:justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openTripStatus(trip);
                                  }}
                                  data-testid={`edit-trip-status-${trip.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Faire avancer
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openTruckState(truck);
                                  }}
                                  disabled={!truck?.id}
                                  data-testid={`edit-truck-state-${trip.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  État du camion
                                </Button>
                              </div>
                              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                                <Clock className="w-4 h-4" />
                                {formatRelativeTime(trip.updated_at)}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
              {sortedTrips.length > 0 ? <BarrePagination pagination={pagination} nom="trajets" /> : null}
            </CardContent>
          </Card>
        }
      />

      {/* Edit trip status */}
      <Dialog open={showTripStatusModal} onOpenChange={setShowTripStatusModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Faire avancer le trajet</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {activeTrip ? (
              <div className="bg-muted/30 rounded-md p-3 border border-border/60">
                <p className="readout-label">
                  État métier actuel
                </p>
                <div className="mt-2">
                  <Badge
                    className={cn(
                      'status-badge',
                      getStatusBadgeClass(activeTrip.status)
                    )}
                  >
                    {libelle('tripStatus', activeTrip.status)}
                  </Badge>
                  {activeTrip && readinessByTrip[activeTrip.id]?.summary && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {texteSur(readinessByTrip[activeTrip.id].summary.next_action, '')}
                    </p>
                  )}
                </div>
              </div>
            ) : null}

            <div>
              <Label className="label-base">Étape possible</Label>
              <Select value={newTripStatus} onValueChange={setNewTripStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir l’étape" />
                </SelectTrigger>
                <SelectContent>
                  {tripStatusOptions.map((s) => (
                    <SelectItem key={s.id || s.action_code} value={s.action_code}>
                      {texteSur(s.action_label, 'Étape suivante')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tripStatusOptions.length === 0 && (
                <p className="mt-2 text-xs text-flame-ink">
                  Aucune étape possible pour le moment. Ouvrez le trajet pour voir ce qui bloque.
                </p>
              )}
            </div>

            <BusinessActionContract
              title="Ce qu’il faut pour cette étape"
              contract={selectedWorkflowAction?.action_contract || tripStatusContract(activeReadiness, newTripStatus)}
              blockers={activeBlockers}
              compact
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTripStatusModal(false)}
            >
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={handleTripStatusUpdate}
              disabled={!newTripStatus || isSubmitting}
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit truck state */}
      <Dialog open={showTruckStateModal} onOpenChange={setShowTruckStateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier l’état du camion</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="label-base">Nouvel état</Label>
              <Select value={newTruckState} onValueChange={setNewTruckState}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un état" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(TRUCK_STATUS_LABELS).map((s) => (
                    <SelectItem key={s} value={s}>
                      {libelle('truckStatus', s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTruckStateModal(false)}
            >
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={handleTruckStateUpdate}
              disabled={!newTruckState || isSubmitting}
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperationDetailPage;
